import React from 'react'
import ReactDOM from 'react-dom/client'
import { BrowserRouter } from 'react-router-dom'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { ConfigProvider, theme, App as AntApp } from 'antd'
import { StyleProvider } from '@ant-design/cssinjs'
import { AnimatePresence } from 'framer-motion'

import App from './App'
import './styles/globals.css'

// Create a client for React Query
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes
      gcTime: 10 * 60 * 1000, // 10 minutes
      refetchOnWindowFocus: false,
      retry: (failureCount, error: any) => {
        if (error?.status === 404 || error?.status === 401) {
          return false
        }
        return failureCount < 3
      }
    },
    mutations: {
      retry: false
    }
  }
})

// Ant Design theme configuration for dark mode
const antdTheme = {
  algorithm: theme.darkAlgorithm,
  token: {
    colorPrimary: '#7c3aed',
    colorBgContainer: '#1e293b',
    colorBgElevated: '#334155',
    colorBgLayout: '#0f172a',
    colorBorder: '#475569',
    colorText: '#f8fafc',
    colorTextSecondary: '#cbd5e1',
    colorBgBase: '#0f172a',
    borderRadius: 8,
    wireframe: false,
    fontSize: 14,
    fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
  },
  components: {
    Card: {
      colorBgContainer: 'rgba(30, 41, 59, 0.8)',
      colorBorderSecondary: 'rgba(71, 85, 105, 0.3)',
    },
    Menu: {
      colorBgContainer: 'rgba(30, 41, 59, 0.95)',
      itemBg: 'transparent',
      itemSelectedBg: 'rgba(124, 58, 237, 0.2)',
      itemHoverBg: 'rgba(124, 58, 237, 0.1)',
    },
    Layout: {
      headerBg: 'rgba(15, 23, 42, 0.95)',
      bodyBg: '#0f172a',
      triggerBg: 'rgba(30, 41, 59, 0.8)',
    },
    Button: {
      primaryShadow: '0 4px 14px 0 rgba(124, 58, 237, 0.4)',
    },
    Table: {
      colorBgContainer: 'rgba(30, 41, 59, 0.6)',
      colorBorderSecondary: 'rgba(71, 85, 105, 0.2)',
    },
    Modal: {
      colorBgElevated: 'rgba(30, 41, 59, 0.95)',
      colorBgMask: 'rgba(0, 0, 0, 0.6)',
    },
    Drawer: {
      colorBgElevated: 'rgba(30, 41, 59, 0.95)',
      colorBgMask: 'rgba(0, 0, 0, 0.6)',
    }
  }
}

ReactDOM.createRoot(document.getElementById('root')).render(
  <React.StrictMode>
    <StyleProvider layer hashPriority="low">
      <QueryClientProvider client={queryClient}>
        <ConfigProvider theme={antdTheme}>
          <AntApp>
            <BrowserRouter>
              <AnimatePresence mode="wait">
                <App />
              </AnimatePresence>
            </BrowserRouter>
          </AntApp>
        </ConfigProvider>
      </QueryClientProvider>
    </StyleProvider>
  </React.StrictMode>,
)

// Service Worker Registration (for PWA capabilities)
// Disabled for now - uncomment when sw.js is properly configured
// if ('serviceWorker' in navigator) {
//   window.addEventListener('load', () => {
//     navigator.serviceWorker.register('/sw.js')
//       .then((registration) => {
//         console.log('SW registered: ', registration);
//       })
//       .catch((registrationError) => {
//         console.log('SW registration failed: ', registrationError);
//       });
//   });
// }

// Global error handler
window.addEventListener('unhandledrejection', (event) => {
  console.error('Unhandled promise rejection:', event.reason);
  // You could send this to an error reporting service
});

window.addEventListener('error', (event) => {
  console.error('Global error:', event.error);
  // You could send this to an error reporting service
});
